/**
 * nexusDiag: the JS face of the NexusDiag native plugin (Android only:
 * android/app/src/main/java/com/nexusdex/app/NexusDiag.java).
 *
 * Two small things the WebView cannot do for itself on a release build:
 *
 *   nativeLog
 *     One line into logcat under a tag of ours. Capacitor forwards WebView
 *     console lines through its Logger, and Logger drops every one of them
 *     unless the build is a debug build (capacitor.config loggingBehavior
 *     defaults to "debug"). So on the release APK the owner sideloads to the
 *     Galaxy, console.* is silent and `adb logcat -s NexusRating` sees
 *     nothing (the 2026-10-26 rating test). The scan code has the same
 *     problem and logs from Java (tag NexusScan); this is the same door for
 *     JS. Fire and forget: a log line never waits on the bridge.
 *
 *   androidInstallSource
 *     Who installed the app: Google Play ("com.android.vending") or not. Play
 *     In-App Review shows its dialog only for an app Play itself installed,
 *     and resolves without a word otherwise, so lib/storeReview asks this
 *     before it counts a resolved call as an ask. Answered once per app
 *     session; the installer does not change while the app runs.
 *
 * Both are best effort and silent where the plugin is absent (iOS, the web, a
 * native build that predates it): a log that cannot be written and an
 * installer that cannot be read must never cost the trainer anything. On iOS
 * a debug build's console still reaches Xcode through Capacitor, and the
 * exported scan log (Settings) carries the same lines on every platform.
 */
import { getPlatform } from "./platform";

export const NEXUS_DIAG_PLUGIN = "NexusDiag";

interface NexusDiagPlugin {
  log(opts: { tag: string; message: string }): Promise<void>;
  installSource(): Promise<{ installer?: string | null; fromPlay?: boolean }>;
}

export interface AndroidInstallSource {
  /** The installing package, e.g. "com.android.vending"; null when the OS has none on record (adb). */
  installer: string | null;
  /** True when Google Play installed this build, so Play can show its sheets. */
  fromPlay: boolean;
}

/** Play's package name, the one installer whose In-App Review dialog can appear. */
export const PLAY_STORE_PACKAGE = "com.android.vending";

let pluginPromise: Promise<NexusDiagPlugin | null> | null = null;

/** The plugin, or null where this build has no native side for it. */
function plugin(): Promise<NexusDiagPlugin | null> {
  if (getPlatform() !== "android") return Promise.resolve(null);
  if (!pluginPromise) {
    pluginPromise = (async () => {
      try {
        const { Capacitor, registerPlugin } = await import("@capacitor/core");
        if (!Capacitor.isPluginAvailable(NEXUS_DIAG_PLUGIN)) return null;
        return registerPlugin<NexusDiagPlugin>(NEXUS_DIAG_PLUGIN);
      } catch {
        return null;
      }
    })();
  }
  return pluginPromise;
}

/**
 * Write one line to the native log (Android logcat) under `tag`. Resolves
 * once the call has been handed off, true when a native side took it.
 * Callers do not await it: `void nativeLog(...)`.
 */
export async function nativeLog(tag: string, message: string): Promise<boolean> {
  try {
    const p = await plugin();
    if (!p || typeof p.log !== "function") return false;
    await p.log({ tag, message });
    return true;
  } catch {
    return false;
  }
}

let installSourcePromise: Promise<AndroidInstallSource | null> | null = null;

/**
 * Where this Android build was installed from, or null when that cannot be
 * known: not Android, or a native side without the plugin (every build before
 * it shipped). Null is "unknown", never "not Play".
 */
export function androidInstallSource(): Promise<AndroidInstallSource | null> {
  if (getPlatform() !== "android") return Promise.resolve(null);
  if (!installSourcePromise) {
    installSourcePromise = (async () => {
      try {
        const p = await plugin();
        if (!p || typeof p.installSource !== "function") return null;
        const res = await p.installSource();
        if (!res || typeof res.fromPlay !== "boolean") return null;
        return { installer: typeof res.installer === "string" ? res.installer : null, fromPlay: res.fromPlay };
      } catch {
        return null;
      }
    })();
  }
  return installSourcePromise;
}

/** Tests only: forget the cached plugin and installer answers. */
export function __resetNexusDiagForTest(): void {
  pluginPromise = null;
  installSourcePromise = null;
}
